/**
 * vehicleParse.ts — WP-06 Part 2.
 *
 * Pure text → { year, make, model, vin } parser for the mobile capture path.
 *
 * A mechanic on a phone gets vehicle details as free text: a customer's SMS,
 * a dealer listing, an insurance card, a photo they retyped.  This module turns
 * that blob into candidate field values WITHOUT ever throwing information away:
 * whatever it could not attribute lands in `problems` (looked like a field but
 * was invalid), `assumptions` (a low-confidence guess it made) or `unparsed`
 * (lines it did not touch).  The UI shows all three so the user can correct it.
 *
 * No network, no AI, no clipboard, no permissions — this runs anywhere.
 */

/** vehicles.make / vehicles.model are max 50 chars server-side (vehicleSchema). */
export const MAX_MAKE_MODEL_LENGTH = 50;

/** Oldest year the server's vehicleSchema accepts. */
export const MIN_VEHICLE_YEAR = 1900;

/**
 * VIN charset: 17 chars from A-H, J-N, P, R-Z and 0-9.
 * I, O and Q are excluded by the VIN standard (they read as 1 / 0).
 */
const VIN_BODY_RE = /^[A-HJ-NPR-Z0-9]{17}$/;
const VIN_LENGTH = 17;

/** Any whitespace, including non-breaking space, which copy/paste loves to add. */
const ANY_WHITESPACE_RE = /[\s ]+/g;

export interface ParsedVehicleInfo {
  vin: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  /** Values that looked like a field but failed validation — always surfaced. */
  problems: string[];
  /** Low-confidence guesses the parser made — always surfaced for confirmation. */
  assumptions: string[];
  /** Input lines the parser did not consume — always surfaced. */
  unparsed: string[];
}

/** Newest year the server's vehicleSchema accepts (derived, never hardcoded). */
export function maxVehicleYear(): number {
  return new Date().getFullYear() + 1;
}

function assertString(value: unknown, fn: string): string {
  if (typeof value !== 'string') {
    throw new TypeError(`${fn}: expected a string, got ${typeof value}`);
  }
  return value;
}

/**
 * Normalise a VIN candidate: uppercase and remove ALL whitespace.
 *
 * Lowercase and embedded spaces are the two things real pastes always contain
 * (`1hg bh4 1jx mn1 091 86`), so they are normalised rather than rejected.
 * Nothing else is stripped — a hyphen or a stray '!' means the value is not a
 * VIN, and silently deleting it would let a wrong VIN through.
 */
export function normalizeVinCandidate(raw: string): string {
  return assertString(raw, 'normalizeVinCandidate')
    .replace(ANY_WHITESPACE_RE, '')
    .toUpperCase();
}

/** True when `raw` normalises to a structurally valid 17-character VIN. */
export function isValidVin(raw: string): boolean {
  assertString(raw, 'isValidVin');
  return VIN_BODY_RE.test(normalizeVinCandidate(raw));
}

/**
 * Explain why a VIN candidate is not a VIN, or null when it is valid.
 * Used to build the user-visible `problems` list.
 */
function vinRejectionReason(raw: string): string | null {
  const clean = normalizeVinCandidate(raw);
  if (VIN_BODY_RE.test(clean)) return null;

  if (clean.length !== VIN_LENGTH) {
    return `"${clean}" looks like a VIN but is ${clean.length} character${
      clean.length === 1 ? '' : 's'
    } — a VIN is exactly ${VIN_LENGTH}.`;
  }
  if (/[IOQ]/.test(clean)) {
    return `"${clean}" contains I, O or Q — those letters never appear in a VIN (they are 1 and 0).`;
  }
  return `"${clean}" is ${VIN_LENGTH} characters but contains characters that are not allowed in a VIN.`;
}

/** Trim, collapse inner whitespace, and drop wrapping punctuation. */
function tidy(value: string): string {
  return value.replace(ANY_WHITESPACE_RE, ' ').trim().replace(/^[,;:]+|[,;:]+$/g, '').trim();
}

/**
 * Cap a make/model at the server's column limit, recording a problem when it
 * had to be shortened so the user can see the value was changed.
 */
function capMakeModel(
  value: string,
  fieldLabel: string,
  problems: string[],
): string {
  if (value.length <= MAX_MAKE_MODEL_LENGTH) return value;
  problems.push(
    `${fieldLabel} was ${value.length} characters and has been shortened to the ${MAX_MAKE_MODEL_LENGTH}-character limit — check it before saving.`,
  );
  return value.slice(0, MAX_MAKE_MODEL_LENGTH);
}

/**
 * Is this token a plausible model year? Returns the year, or records a problem
 * and returns null when a 4-digit number is out of the acceptable range.
 */
function readYear(token: string, problems: string[]): number | null {
  const year = parseInt(token, 10);
  if (Number.isNaN(year)) return null;
  const max = maxVehicleYear();
  if (year < MIN_VEHICLE_YEAR || year > max) {
    problems.push(
      `${token} is not a usable vehicle year (must be between ${MIN_VEHICLE_YEAR} and ${max}).`,
    );
    return null;
  }
  return year;
}

const LABEL_RE = /^\s*(vin|year|yr|make|model|mdl)\b\s*[:=]?\s*(.*)$/i;
const FOUR_DIGIT_RE = /^\d{4}$/;
/** A token that could be a mangled VIN: right ballpark length, letters + digits. */
const VIN_LIKE_RE = /^(?=.*[0-9])(?=.*[A-Za-z])[A-Za-z0-9]{15,19}$/;

interface Accumulator {
  vin: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  problems: string[];
  assumptions: string[];
}

/** Apply a labeled `key: value` line. Returns false when the line was not consumed. */
function applyLabeled(key: string, rawValue: string, acc: Accumulator): boolean {
  const value = tidy(rawValue);
  if (value === '') return false;

  const lower = key.toLowerCase();

  if (lower === 'vin') {
    const reason = vinRejectionReason(value);
    if (reason) {
      acc.problems.push(reason);
      return true; // consumed: we told the user exactly what was wrong with it
    }
    acc.vin = normalizeVinCandidate(value);
    return true;
  }

  if (lower === 'year' || lower === 'yr') {
    if (!FOUR_DIGIT_RE.test(value)) {
      acc.problems.push(`"${value}" is not a 4-digit year.`);
      return true;
    }
    const year = readYear(value, acc.problems);
    if (year !== null) acc.year = year;
    return true;
  }

  if (lower === 'make') {
    acc.make = capMakeModel(value, 'Make', acc.problems);
    return true;
  }

  // model / mdl
  acc.model = capMakeModel(value, 'Model', acc.problems);
  return true;
}

/**
 * Pull a VIN out of a free-text line.
 * Returns the line with the VIN token removed, or null when the line held none.
 */
function extractVinFromLine(line: string, acc: Accumulator): string | null {
  // Case 1: the whole line is a VIN, possibly split by spaces ("1HG BH41JX MN109186").
  const compact = normalizeVinCandidate(line);
  if (compact.length === VIN_LENGTH && VIN_BODY_RE.test(compact)) {
    if (acc.vin === null) acc.vin = compact;
    return '';
  }

  // Case 2: one token inside the line is a VIN.
  const tokens = line.split(ANY_WHITESPACE_RE).filter((t) => t !== '');
  for (let i = 0; i < tokens.length; i += 1) {
    const bare = tokens[i].replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
    // Exactly 17 chars in the VIN charset IS a VIN by structure — no extra
    // letter/digit heuristic here, or an all-digit VIN inside a sentence is missed.
    if (bare.length !== VIN_LENGTH) continue;
    if (isValidVin(bare)) {
      if (acc.vin === null) acc.vin = normalizeVinCandidate(bare);
      const rest = [...tokens.slice(0, i), ...tokens.slice(i + 1)];
      return rest.join(' ');
    }
  }
  return null;
}

/** Report tokens that look like a botched VIN so the paste is never silently ignored. */
function reportVinLikeTokens(line: string, acc: Accumulator): void {
  if (acc.vin !== null) return;
  for (const token of line.split(ANY_WHITESPACE_RE)) {
    const bare = token.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
    if (!VIN_LIKE_RE.test(bare) || isValidVin(bare)) continue;
    const reason = vinRejectionReason(bare);
    if (reason) acc.problems.push(reason);
  }
}

/**
 * Read year / make / model out of a year-anchored line.
 * Returns true when the line was consumed.
 *
 * Conventional ordering is "2012 Chevy Silverado", so make/model are read from
 * the tokens AFTER the year and stop at a comma (which ends the vehicle clause).
 * A trailing year ("Honda Civic 2018") falls back to the tokens before it.
 */
function applyYearAnchoredLine(line: string, acc: Accumulator): boolean {
  const tokens = line.split(ANY_WHITESPACE_RE).filter((t) => t !== '');
  const yearIdx = tokens.findIndex((t) => FOUR_DIGIT_RE.test(t.replace(/[,;.]+$/, '')));
  if (yearIdx === -1) return false;

  const yearToken = tokens[yearIdx].replace(/[,;.]+$/, '');
  const year = readYear(yearToken, acc.problems);
  if (year !== null && acc.year === null) acc.year = year;

  // Tokens after the year, up to the first comma-terminated token.
  const after: string[] = [];
  for (let i = yearIdx + 1; i < tokens.length; i += 1) {
    const token = tokens[i];
    const bare = tidy(token);
    if (bare === '') continue;
    after.push(bare);
    if (/[,;]$/.test(token)) break;
  }

  const before = tokens.slice(0, yearIdx).map(tidy).filter((t) => t !== '');
  const words = after.length > 0 ? after : before;

  if (words.length === 0) {
    // A bare year and nothing else — consumed only if we actually took the year.
    return year !== null;
  }

  if (acc.make === null && words.length >= 1) {
    acc.make = capMakeModel(words[0], 'Make', acc.problems);
  }
  if (acc.model === null && words.length >= 2) {
    acc.model = capMakeModel(words.slice(1).join(' '), 'Model', acc.problems);
  }
  return true;
}

/**
 * Parse a free-text blob into vehicle fields.
 *
 * Throws TypeError on a non-string input — a silent empty result would look
 * like "your paste contained nothing", which is a lie the user cannot debug.
 */
export function parseVehicleText(text: string): ParsedVehicleInfo {
  const input = assertString(text, 'parseVehicleText');

  const acc: Accumulator = {
    vin: null,
    year: null,
    make: null,
    model: null,
    problems: [],
    assumptions: [],
  };

  const lines = input
    .split(/\r\n|\r|\n/)
    .map((l) => l.replace(/[ ]/g, ' '))
    .filter((l) => l.trim() !== '');

  // Pass 1 — labeled lines win over positional guessing.
  const remaining: string[] = [];
  for (const line of lines) {
    const match = LABEL_RE.exec(line);
    if (match && applyLabeled(match[1], match[2], acc)) continue;
    remaining.push(line);
  }

  // Pass 2 — VIN then year/make/model out of the unlabeled remainder.
  const leftovers: string[] = [];
  for (const line of remaining) {
    const withoutVin = extractVinFromLine(line, acc);
    const rest = withoutVin === null ? line : withoutVin;
    if (withoutVin !== null && rest.trim() === '') continue; // line was only a VIN

    if (applyYearAnchoredLine(rest, acc)) continue;

    reportVinLikeTokens(rest, acc);
    // The VIN was taken from this line even though the rest was not understood —
    // keep only the un-understood remainder so nothing is hidden.
    leftovers.push(withoutVin === null ? line : rest);
  }

  // Pass 3 — a single leftover line of exactly two words, the first purely
  // alphabetic, is very likely "Make Model" ("Honda Civic", "Ford F-150").
  // Guess it, but say out loud that it was a guess.
  //
  // Deliberately narrow: three-plus words is ordinary prose ("some unrelated
  // note") and guessing there would put junk in the make field, which reads as
  // the parser having "understood" something it did not.
  if (acc.make === null && acc.model === null && leftovers.length === 1) {
    const words = leftovers[0].split(ANY_WHITESPACE_RE).map(tidy).filter((w) => w !== '');
    if (words.length === 2 && /^[A-Za-z][A-Za-z.&'-]*$/.test(words[0])) {
      acc.make = capMakeModel(words[0], 'Make', acc.problems);
      acc.model = capMakeModel(words.slice(1).join(' '), 'Model', acc.problems);
      acc.assumptions.push(
        `Read "${leftovers[0]}" as make + model — no year was found, so please confirm it.`,
      );
      leftovers.length = 0;
    }
  }

  return {
    vin: acc.vin,
    year: acc.year,
    make: acc.make,
    model: acc.model,
    problems: acc.problems,
    assumptions: acc.assumptions,
    unparsed: leftovers,
  };
}
